import React, { useState, useEffect, useCallback } from 'react';
import { Columns, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/**
 * useColumnPicker
 *
 * Hook + trigger button for hide/show table columns, persisted per user
 * per table via localStorage.
 *
 *   const cols = useColumnPicker('products', DEFAULT_COLS);
 *   cols.isVisible('vendor')  // -> true/false
 *   cols.Trigger              // <ColumnPickerTrigger /> component
 *
 * DEFAULT_COLS is an array of { key, label, defaultVisible?: true }.
 */
export function useColumnPicker(storageKey, defaultCols) {
  const [visible, setVisible] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(`lodgiflow_cols_${storageKey}`) || 'null');
      if (stored && typeof stored === 'object') {
        // strip keys that no longer exist so old prefs don't stick
        const cleaned = {};
        defaultCols.forEach(c => { cleaned[c.key] = c.key in stored ? !!stored[c.key] : (c.defaultVisible !== false); });
        return cleaned;
      }
    } catch {}
    const initial = {};
    defaultCols.forEach(c => { initial[c.key] = c.defaultVisible !== false; });
    return initial;
  });

  useEffect(() => {
    try { localStorage.setItem(`lodgiflow_cols_${storageKey}`, JSON.stringify(visible)); } catch {}
  }, [storageKey, visible]);

  const isVisible = useCallback((key) => visible[key] !== false, [visible]);
  const toggle = useCallback((key) => setVisible(v => ({ ...v, [key]: !v[key] })), []);
  const reset = useCallback(() => {
    const initial = {};
    defaultCols.forEach(c => { initial[c.key] = c.defaultVisible !== false; });
    setVisible(initial);
  }, [defaultCols]);

  const Trigger = (props = {}) => (
    <ColumnPickerTrigger
      cols={defaultCols}
      visible={visible}
      onToggle={toggle}
      onReset={reset}
      {...props}
    />
  );

  return { isVisible, toggle, reset, Trigger, visible };
}

export function ColumnPickerTrigger({ cols, visible, onToggle, onReset, className = '' }) {
  const shown = cols.filter(c => visible[c.key] !== false).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`h-8 gap-1.5 text-xs ${className}`}
          data-testid="column-picker-trigger"
        >
          <Columns className="h-3.5 w-3.5" />
          Columns
          <span className="text-muted-foreground">({shown}/{cols.length})</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1.5" data-testid="column-picker-popover">
        <div className="px-2 py-1.5 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Columns</span>
          <button
            className="text-[10px] text-violet-600 hover:underline"
            onClick={onReset}
            data-testid="column-picker-reset"
          >
            Reset
          </button>
        </div>
        <div className="space-y-0.5">
          {cols.map(c => {
            const on = visible[c.key] !== false;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => onToggle(c.key)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-left ${on ? 'bg-muted/40' : ''} hover:bg-muted`}
                data-testid={`column-picker-toggle-${c.key}`}
              >
                <span className={`inline-flex items-center justify-center h-4 w-4 rounded-sm border ${on ? 'border-violet-500 bg-violet-500 text-white' : 'border-muted-foreground/40'}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1">{c.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
